import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import logoPng from "../img/logoWhite.png";

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, Building2, Globe } from 'lucide-react';

import { auth, db } from '@/lib/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

const AuthPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t, i18n } = useTranslation();

  // language toggle now powered by i18next
  const language = (i18n.language || 'en').slice(0, 2);
  const toggleLanguage = () => {
    const current = i18n.language || 'en';
    const next = current.startsWith('en') ? 'fr' : 'en';
    i18n.changeLanguage(next);
  };

  const [isLoading, setIsLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    profession: '',
    country: '',
    city: '',
  });

  // If already logged in, redirect to home
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        loginData.email,
        loginData.password
      );

      const fbUser = credential.user;

      if (!fbUser.emailVerified) {
        try {
          await updateDoc(doc(db, 'users', fbUser.uid), {
            email_verified: fbUser.emailVerified,
          });
        } catch (err) {
          console.warn('Failed to update email_verified in Firestore:', err);
        }

        try {
          await sendEmailVerification(fbUser, {
            url: `${window.location.origin}/auth`,
          });
          toast.error(
            'Your email is not verified. We just sent you another verification email. Please check your inbox.'
          );
        } catch (err) {
          console.error('Error sending verification email:', err);
          toast.error(
            'Your email is not verified. Please check your inbox for the verification email.'
          );
        }

        await signOut(auth);
        return;
      }

      try {
        await updateDoc(doc(db, 'users', fbUser.uid), {
          email_verified: fbUser.emailVerified,
        });
      } catch (err) {
        console.warn('Failed to update email_verified in Firestore:', err);
      }

      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      console.error(error);
      let message = 'Login failed';
      if (error.code === 'auth/user-not-found') {
        message = 'No account found with that email';
      } else if (error.code === 'auth/wrong-password') {
        message = 'Incorrect password';
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const {
      email,
      password,
      confirm_password,
      first_name,
      last_name,
      profession,
      country,
      city,
    } = registerData;

    if (password !== confirm_password) {
      toast.error('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const fbUser = userCredential.user;

      await updateProfile(fbUser, {
        displayName: `${first_name} ${last_name}`.trim(),
      });

      await setDoc(doc(db, 'users', fbUser.uid), {
        id: fbUser.uid,
        email: fbUser.email,
        first_name,
        last_name,
        profession: profession || null,
        country: country || null,
        city: city || null,
        profile_photo: fbUser.photoURL || null,
        email_verified: fbUser.emailVerified || false,
        created_at: new Date().toISOString(),
      });

      await sendEmailVerification(fbUser, {
        url: `${window.location.origin}/auth`,
      });

      toast.success(
        'Account created! We sent you a verification email. Please verify your email before logging in.'
      );

      await signOut(auth);
      navigate('/auth');
    } catch (error) {
      console.error(error);
      let message = 'Registration failed';
      if (error.code === 'auth/email-already-in-use') {
        message = 'Email already in use';
      } else if (error.code === 'auth/weak-password') {
        message = 'Password is too weak';
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!loginData.email) {
      toast.error('Please enter your email address first.');
      return;
    }
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, loginData.email, {
        url: `${window.location.origin}/auth`,
      });
      toast.success('Password reset email sent. Please check your inbox.');
    } catch (error) {
      console.error(error);
      let message = 'Failed to send password reset email.';
      if (error.code === 'auth/user-not-found') {
        message = 'No account found with that email.';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Invalid email address.';
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #ca7b14ff 0%, #eec043e8 100%)',
      }}
    >
      {/* Language Switcher (now via i18next) */}
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleLanguage}
        className="absolute top-4 right-4 text-white hover:bg-white/20 flex items-center space-x-2"
        data-testid="auth-language-toggle"
      >
        <Globe size={16} />
        <span className="font-semibold">{language.toUpperCase()}</span>
      </Button>

      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-8 items-center">
        {/* Left side - Branding */}
        <div className="hidden md:flex flex-col space-y-6 text-white">
          <div className="flex items-center space-x-3">
            <div className="w-40 h-40 flex items-center justify-center">
              <img
                src={logoPng}
                alt="Logo"
                className="w-30 h-30 object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold">ZiiHUB</h1>
          </div>
          <p className="text-xl text-white/90 leading-relaxed">
            Build and manage thriving communities with powerful multi-tenant platform
          </p>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users size={16} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Multi-Group Management</h3>
                <p className="text-white/80 text-sm">
                  Create and manage multiple community groups
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 size={16} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Events &amp; Store</h3>
                <p className="text-white/80 text-sm">
                  Host events and sell merchandise
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Auth form */}
        <Card className="shadow-2xl border-0">
          <CardHeader>
            <CardTitle className="text-2xl">{t('welcome')}</CardTitle>
            <CardDescription>{t('signInAccount')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="login" data-testid="login-tab">
                  {t('signIn')}
                </TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">
                  {t('register')}
                </TabsTrigger>
              </TabsList>

              {/* Login */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">{t('email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder=" "
                      data-testid="login-email-input"
                      value={loginData.email}
                      onChange={(e) =>
                        setLoginData({ ...loginData, email: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">{t('password')}</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      data-testid="login-password-input"
                      value={loginData.password}
                      onChange={(e) =>
                        setLoginData({
                          ...loginData,
                          password: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    data-testid="login-submit-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? t('loading') : t('signIn')}
                  </Button>
                </form>
              </TabsContent>

              {/* Register */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first-name">{t('firstName')}</Label>
                      <Input
                        id="first-name"
                        placeholder="John"
                        data-testid="register-firstname-input"
                        value={registerData.first_name}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            first_name: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="last-name">{t('lastName')}</Label>
                      <Input
                        id="last-name"
                        placeholder="Doe"
                        data-testid="register-lastname-input"
                        value={registerData.last_name}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            last_name: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-email">{t('email')}</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="your@email.com"
                      data-testid="register-email-input"
                      value={registerData.email}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          email: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-password">{t('password')}</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      data-testid="register-password-input"
                      value={registerData.password}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          password: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-confirm-password">
                      {t('confirmPassword') || 'Confirm password'}
                    </Label>
                    <Input
                      id="register-confirm-password"
                      type="password"
                      placeholder="••••••••"
                      data-testid="register-confirmpassword-input"
                      value={registerData.confirm_password}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          confirm_password: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="profession">
                      {t('profession')} ({t('optional')})
                    </Label>
                    <Input
                      id="profession"
                      placeholder="Software Engineer"
                      data-testid="register-profession-input"
                      value={registerData.profession}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          profession: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="country">
                        {t('country')} ({t('optional')})
                      </Label>
                      <Input
                        id="country"
                        placeholder="USA"
                        data-testid="register-country-input"
                        value={registerData.country}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            country: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">
                        {t('city')} ({t('optional')})
                      </Label>
                      <Input
                        id="city"
                        placeholder="New York"
                        data-testid="register-city-input"
                        value={registerData.city}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            city: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    data-testid="register-submit-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? t('loading') : t('createAccount')}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
